import { useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import { Divider, Typography, Alert, Paper, CircularProgress } from "@mui/material";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Container from "@mui/material/Container";
import ConstructionIcon from "@mui/icons-material/Construction";
import * as React from "react";

import { useSnackbar } from "notistack";
import Swal from "sweetalert2";
import { registerNewUser, createUserDocument } from "../../utils/firebaseUtils";
import { getAuth, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { APP_CONFIG } from "../../config/appConfig";
import { useAuth } from "../../firebase";

export const isValidEmail = (email: string) => {
  if (/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
    return true;
  }
  return false;
};

export default function SignUpPage() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = React.useState(false);

  // Redirect to home if user is already signed in
  React.useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  const handleSubmit = (event: any) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const registration = {
      firstName: data.get("firstName"),
      lastName: data.get("lastName"),
      email: data.get("email"),
      password: data.get("password"),
    };
    validateFieldsAndSubmit(registration);
  };

  const showErrorMessage = (message: string) => {
    enqueueSnackbar(message, {
      variant: "error",
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left",
      },
    });
  };

  const validateFieldsAndSubmit = (registration: any) => {
    if (!isValidEmail(registration.email)) {
      showErrorMessage("Please enter a valid email address");
      return;
    }
    if (registration.password.length < 5) {
      showErrorMessage("Please enter a longer password");
      return;
    }
    if (registration.firstName.length < 1 || registration.lastName.length < 1) {
      showErrorMessage("Please enter your first and last name");
      return;
    }
    submitRegistration(registration);
  };

  const successCallback = (data: any) => {
    setLoading(false);
    Swal.fire({
      title: "Registration Successful",
      text: "Registration submitted succesfully! Check your email for validation.",
      icon: "success",
      confirmButtonText: "OKAY",
    }).then(() => {
      navigate("/sign-in");
    });
  };

  const failureCallback = (data: any) => {
    setLoading(false);
    showErrorMessage(data);
  };

  const submitRegistration = (registration: any) => {
    setLoading(true);
    registerNewUser(registration, successCallback, failureCallback);
  };

  const handleGoogleSignUp = () => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    setLoading(true);

    signInWithPopup(auth, provider)
      .then(async (result) => {
        // Create user document in Firestore if it doesn't exist
        await createUserDocument(result.user);

        // Successfully signed up with Google
        Swal.fire({
          title: "Sign Up Successful",
          text: "Welcome! You've signed up with Google.",
          icon: "success",
          confirmButtonText: "OKAY",
        }).then(() => {
          navigate("/");
        });
      })
      .catch((error) => {
        showErrorMessage("Error signing up with Google: " + error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Don't render anything if user is already authenticated
  if (user) {
    return null;
  }

  // If sign-ups are disabled, show development notice
  if (!APP_CONFIG.ALLOW_SIGNUPS) {
    return (
      <Container component="main" maxWidth="sm">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "warning.main" }}>
            <ConstructionIcon />
          </Avatar>
          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            Sign Up Temporarily Disabled
          </Typography>

          <Paper elevation={3} sx={{ p: 3, width: "100%" }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {APP_CONFIG.MESSAGES.DEVELOPMENT_MODE}
              </Typography>
              <Typography variant="body1" paragraph>
                {APP_CONFIG.MESSAGES.SIGNUP_DISABLED}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                If you already have an account, you can still sign in below.
              </Typography>
            </Alert>

            <Box sx={{ textAlign: "center", mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/sign-in")}
                sx={{ mr: 2 }}
              >
                Sign In Instead
              </Button>
              <Button variant="outlined" onClick={() => navigate("/")}>
                Back to Home
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Original sign-up form (only shown if ALLOW_SIGNUPS is true)
  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: "primary.main" }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Sign Up
        </Typography>
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                autoComplete="given-name"
                name="firstName"
                required
                fullWidth
                id="firstName"
                label="First Name"
                autoFocus
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                id="lastName"
                label="Last Name"
                name="lastName"
                autoComplete="family-name"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="new-password"
              />
            </Grid>
          </Grid>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Sign Up"}
          </Button>
          <Divider sx={{ my: 2 }}>OR</Divider>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleGoogleSignUp}
            sx={{ mb: 2 }}
            disabled={loading}
          >
            Sign Up with Google
          </Button>
          <Grid container justifyContent="flex-end">
            <Grid>
              <Link href="/sign-in" variant="body2">
                Already have an account? Sign in
              </Link>
            </Grid>
          </Grid>
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 2 }}
          >
            By signing up you agree to our{" "}
            <Link href="/terms-of-service">Terms of Service</Link> and{" "}
            <Link href="/privacy-policy">Privacy Policy</Link>.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
}
